import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, AlertCircle, Loader } from 'lucide-react'
import { AgentHistoryService } from '../services/agentHistoryService'
import { useAuth } from '../hooks/useSupabaseAuth.jsx'
// useLLM hook removed with search/chat features
import { AGENT_TYPES } from '../constants/agentConfig.js'
import { buttonVariants, componentClasses, cn } from '../utils/designSystem'

// Import agent result components
import ArtikelResultPage from '../agents/ArtikelAgent/ArtikelResultPage'
import SkripsiResultPage from '../agents/SkripsiAgent/SkripsiResultPage'
import MakalahResultPage from '../agents/MakalahAgent/MakalahResultPage'
import GambarResultPage from '../agents/GambarAgent/GambarResultPage'
import VideoResultPage from '../agents/VideoAgent/VideoResultPage'
import BukuResultPage from '../agents/BukuAgent/BukuResultPage'
import NovelResultPage from '../agents/NovelAgent/NovelResultPage'
import BeritaResultPage from '../agents/BeritaAgent/BeritaResultPage'
import SearchResultPage from '../agents/SearchAgent/SearchResultPage'
import TrendingResultPage from '../agents/TrendingAgent/TrendingResultPage'
import WritingResultPage from '../agents/WritingAgent/WritingResultPage'
import PPTResultPage from '../agents/PPTAgent/PPTResultPage'
import ChartResultPage from '../agents/ChartAgent/ChartResultPage'
import ResearchResultPage from '../agents/ResearchAgent/ResearchResultPage'
import PDFResultPage from '../agents/PDFAgent/PDFResultPage'

const AgentResultViewPage = () => {
  const { agentType, id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [agentResult, setAgentResult] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // LLM service removed with search/chat features

  useEffect(() => {
    const loadAgentResult = async () => {
      if (!user) {
        setError('User not authenticated')
        setLoading(false)
        return
      }

      if (!id) {
        setError('No result ID provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        

        const result = await AgentHistoryService.getAgentHistoryDetail(id)
        
        if (result.error) {
          throw new Error(result.error)
        }
        
        if (!result.data) {
          throw new Error('Agent result not found')
        }
        
        // Verify agent type matches (handle both URL and database formats)
        const normalizeAgentType = (type) => {
          if (type.endsWith('_agent')) return type
          return `${type}_agent`
        }
        
        const normalizedUrlType = normalizeAgentType(agentType)
        const normalizedDbType = normalizeAgentType(result.data.agent_type)
        const isValidAgentType = normalizedUrlType === normalizedDbType
        
        if (!isValidAgentType) {
          throw new Error(`Agent type mismatch. Expected: ${agentType}, Got: ${result.data.agent_type}`)
        }
        
        setAgentResult(result.data)
        
        // Update last_accessed timestamp
        await AgentHistoryService.updateAgentResult(id, {
          last_accessed: new Date().toISOString()
        })
        
      } catch (err) {
        console.error('Error loading agent result:', err)
        setError(err.message || 'Failed to load agent result')
      } finally {
        setLoading(false)
      }
    }

    loadAgentResult()
  }, [id, agentType, user])

  const handleBack = () => {
    navigate('/agent-history')
  }

  const handleBackToSearch = () => {
    navigate('/')
  }

  // Show loading state
  if (loading) {
    return (
      <div className={cn(componentClasses.main, "min-h-screen p-6")}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader className="w-8 h-8 animate-spin text-accent-primary mx-auto mb-4" />
              <p className="text-text-secondary">Loading agent result...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className={cn(componentClasses.main, "min-h-screen p-6")}>
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-2 text-text-tertiary hover:text-text-primary transition-colors rounded-lg hover:bg-bg-tertiary"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Kembali</span>
            </button>
          </div>
          
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-md">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-text-primary mb-2">Error Loading Result</h2>
              <p className="text-text-secondary mb-4">{error}</p>
              <button
                onClick={handleBack}
                className={cn(buttonVariants.primary)}
              >
                Back to Agent History
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show not found state
  if (!agentResult) {
    return (
      <div className={cn(componentClasses.main, "min-h-screen p-6")}>
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-2 text-text-tertiary hover:text-text-primary transition-colors rounded-lg hover:bg-bg-tertiary"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Kembali</span>
            </button>
          </div>
          
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-md">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-text-primary mb-2">Result Not Found</h2>
              <p className="text-text-secondary mb-4">The requested agent result could not be found.</p>
              <button
                onClick={handleBack}
                className={cn(buttonVariants.primary)}
              >
                Back to Agent History
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Prepare data for agent result components
  const { content, sources = [], metadata = {} } = agentResult
  const { outline = [], generatedContent = {} } = content || {}
  
  // Common props for all agent result components
  const commonProps = {
    query: agentResult.query,
    outline: outline,
    generatedContent: generatedContent,
    academicSources: sources,
    progress: metadata.progress || { current: outline.length, total: outline.length },
    isLoading: false,
    error: null,
    isCompleted: true,
    onBack: handleBack,
    onBackToSearch: handleBackToSearch
  }

  // Render appropriate agent result component based on agent type
  const renderAgentResult = () => {
    // Normalize agentType to ensure consistency
    const normalizedAgentType = agentType.endsWith('_agent') ? agentType : `${agentType}_agent`
    
    switch (normalizedAgentType) {
      case AGENT_TYPES.ARTIKEL:
      case 'artikel_agent': // Handle legacy database values
        return <ArtikelResultPage {...commonProps} />
      
      case AGENT_TYPES.SKRIPSI:
      case 'skripsi_agent': // Handle legacy database values
        return <SkripsiResultPage {...commonProps} />
      
      case AGENT_TYPES.MAKALAH:
      case 'makalah_agent': // Handle legacy database values
        return <MakalahResultPage {...commonProps} />
      
      case AGENT_TYPES.GAMBAR:
      case 'gambar_agent': // Handle legacy database values
        // For Gambar agent, we need different props structure
        const images = content?.images || []
        const firstImage = images.length > 0 ? images[0] : null
        const settings = content?.settings || {}
        
        const gambarProps = {
          query: agentResult.query,
          generatedImage: firstImage,
          aspectRatio: settings.aspectRatio || '1:1',
          isLoading: false,
          error: null,
          isCompleted: true,
          onBack: handleBack,
          onRegenerateImage: () => {}
        }
        return <GambarResultPage {...gambarProps} />
      
      case AGENT_TYPES.VIDEO:
      case 'video_agent': // Handle legacy database values
        // For Video agent, we need different props structure
        const videos = content?.videos || []
        const firstVideo = videos.length > 0 ? videos[0] : null
        const videoSettings = content?.settings || {}
        
        const videoProps = {
          query: agentResult.query,
          generatedVideo: firstVideo,
          aspectRatio: videoSettings.aspectRatio || metadata?.aspectRatio || '16:9',
          videoType: content?.videoType || metadata?.videoType || 'text_to_video',
          isLoading: false,
          error: null,
          isCompleted: true,
          onBack: handleBack,
          onRegenerateVideo: () => {}
        }
        return <VideoResultPage {...videoProps} />
      
      case AGENT_TYPES.BUKU:
      case 'buku_agent': // Handle legacy database values
        return <BukuResultPage {...commonProps} />
      
      case AGENT_TYPES.NOVEL:
      case 'novel_agent': // Handle legacy database values
        return <NovelResultPage {...commonProps} />
      
      case AGENT_TYPES.BERITA:
      case 'berita_agent': // Handle legacy database values
        // For Berita agent, we need different props structure
        const beritaProps = {
          query: agentResult.query,
          selectedOption: metadata?.selectedOption,
          generatedContent: content?.generatedContent || content?.fullText || '',
          newsSources: sources || [],
          progress: metadata?.progress || { current: 1, total: 1 },
          isLoading: false,
          error: null,
          isCompleted: true,
          language: metadata?.language || 'id',
          onBack: handleBack,
          onBackToSearch: handleBackToSearch
        }
        return <BeritaResultPage {...beritaProps} />
      
      case AGENT_TYPES.SEARCH:
      case 'search_agent': // Handle legacy database values
        // For Search agent, we need different props structure
        const searchProps = {
          query: agentResult.query,
          searchResult: {
            answer: content?.answer || content?.generatedContent || '',
            sources: sources || [],
            images: content?.images || []
          },
          searchType: content?.searchType || metadata?.searchType || 'web',
          onBack: handleBack,
          onRetry: () => {}
        }
        return <SearchResultPage {...searchProps} />
      
      case AGENT_TYPES.TRENDING:
      case 'trending_agent': // Handle legacy database values
        // For Trending agent, we need different props structure
        const trendingProps = {
          query: agentResult.query,
          trendingResult: {
            answer: content?.answer || '',
            trendingItems: content?.trendingItems || [],
            sources: sources || [],
            images: content?.images || [],
            region: content?.region || metadata?.region || 'Indonesia',
            enhancedWithLLM: metadata?.enhancedWithLLM || false,
            query: agentResult.query
          },
          dateRange: {
            fromDate: content?.dateRange?.start || metadata?.fromDate || new Date().toISOString().split('T')[0],
            toDate: content?.dateRange?.end || metadata?.toDate || new Date().toISOString().split('T')[0]
          },
          onBack: handleBack,
          onRetry: () => {}
        }
        return <TrendingResultPage {...trendingProps} />
      
      case AGENT_TYPES.WRITING:
      case 'writing_agent': // Handle legacy database values
        // For Writing agent, we need different props structure
        const writingProps = {
          query: agentResult.query,
          title: content?.title || '',
          content: content?.content || '',
          onBack: handleBack,
          onBackToSearch: handleBackToSearch
        }
        return <WritingResultPage {...writingProps} />
      
      case AGENT_TYPES.PPT:
      case 'ppt_agent': // Handle legacy database values
        return <PPTResultPage {...commonProps} />
      
      case AGENT_TYPES.CHART:
      case 'chart_agent': // Handle legacy database values
        // For Chart agent, we need different props structure
        const chartProps = {
          query: agentResult.query,
          inputType: content?.inputType || metadata?.inputType || 'topic',
          inputData: content?.inputData || '',
          generatedCharts: content?.generatedCharts || [],
          progress: metadata?.progress || { current: 1, total: 1 },
          isLoading: false,
          isCompleted: true,
          onBack: handleBack,
          onRegenerateChart: () => {},
          error: null,
          searchResults: content?.searchResults || []
        }
        return <ChartResultPage {...chartProps} />
      
      case AGENT_TYPES.RESEARCH:
      case 'research_agent': // Handle legacy database values
        // For Research agent, we need different props structure
        const researchProps = {
          result: {
            researchPaper: content?.researchPaper || {},
            academicSources: sources || [],
            relevantImages: content?.relevantImages || [],
            chartImages: content?.chartImages || [],
            query: agentResult.query
          },
          onBack: handleBack
        }
        return <ResearchResultPage {...researchProps} />
      
      case AGENT_TYPES.PDF:
      case 'pdf_agent': // Handle legacy database values
        // For PDF agent, we need different props structure
        const pdfProps = {
          query: agentResult.query,
          analysisResult: {
            analysis: typeof content?.analysis === 'string' ? content.analysis : (typeof content?.analysis === 'object' ? JSON.stringify(content.analysis, null, 2) : ''),
            summary: content?.summary || '',
            keyPoints: content?.keyPoints || [],
            fileName: content?.fileName || metadata?.fileName || 'document.pdf',
            extractedTextLength: content?.extractedTextLength || metadata?.extractedTextLength || 0,
            model: content?.mode || metadata?.mode || 'AI Model',
            generatedAt: agentResult.created_at || new Date().toISOString()
          },
          pdfFile: {
            name: content?.fileName || metadata?.fileName || 'document.pdf',
            size: content?.fileSize || metadata?.fileSize || 0
          },
          isLoading: false,
          error: null,
          isCompleted: true,
          onBack: handleBack,
          onReanalyze: () => {}
        }
        return <PDFResultPage {...pdfProps} />
      
      default:
        return (
          <div className={cn(componentClasses.main, "min-h-screen p-6")}>
            <div className="max-w-6xl mx-auto">
              <div className="mb-6">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-3 py-2 text-text-tertiary hover:text-text-primary transition-colors rounded-lg hover:bg-bg-tertiary"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm font-medium">Kembali</span>
                </button>
              </div>
              
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center max-w-md">
                  <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-text-primary mb-2">Unsupported Agent Type</h2>
                  <p className="text-text-secondary mb-4">
                    Agent type "{normalizedAgentType}" is not supported for viewing.
                  </p>
                  <button
                    onClick={handleBack}
                    className={cn(buttonVariants.primary)}
                  >
                    Back to Agent History
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  return renderAgentResult()
}

export default AgentResultViewPage